import Checkbox from 'components/Forms/Checkbox/Checkbox';
import { DateSelector } from 'components/Forms/DateSelector/DateSelector';
import ChoiceFormLine from 'components/Forms/Lib/ChoiceFormLine/ChoiceFormLine';
import FormColumn from 'components/Forms/Lib/FormColumn/FormColumn';
import FormLine from 'components/Forms/Lib/FormLine/FormLine';
import FormLineError from 'components/Forms/Lib/FormLineError/FormLineError';
import { PhoneInput } from 'components/Forms/PhoneInput/PhoneInput';
import Select from 'components/Forms/Select/Select';
import TextInput from 'components/Forms/TextInput/TextInput';
import { RegistrationFormType, useRegistrationFormMeta } from 'components/Pages/Registration/formMeta';
import { useCountriesAsSelectOptions } from 'connectors/country/Country';
import { useDistrictsAsSelectOptions } from 'connectors/Settings/useDistrictsAsSelectOptions';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { createNumberAriaLabel } from 'helpers/accessibility/ariaLabelHelpers';
import { B2C_IT_DOMAIN_ID, isDomain, isDomainLocale } from 'helpers/isDomainLocale';
import { useCustomerUserAddressFormFieldOrder } from 'hooks/forms/useCustomerUserAddressFormFieldOrder';
import { useCustomerUserAgeValidationConstants } from 'hooks/forms/useCustomerUserAgeValidationConstants';
import useTranslation from 'next-translate/useTranslation';
import React, { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

const User: FC = () => {
    const { t } = useTranslation();
    const { minimalDate, maximalDate } = useCustomerUserAgeValidationConstants();
    const formProviderMethods = useFormContext<RegistrationFormType>();
    const { setValue } = formProviderMethods;
    const formMeta = useRegistrationFormMeta(formProviderMethods);
    const countrySelectOptions = useCountriesAsSelectOptions();
    const districtSelectOptions = useDistrictsAsSelectOptions();
    const { defaultLocale, domainId } = useDomainConfig();
    const isItalianB2c = isDomain(domainId, B2C_IT_DOMAIN_ID);
    const isPortugalLocale = isDomainLocale(defaultLocale, 'pt');
    const isAustriaLocale = isDomainLocale(defaultLocale, 'de-AT');

    const { city: cityFieldOrder } = useCustomerUserAddressFormFieldOrder();

    useEffect(() => {
        if (countrySelectOptions.length > 0) {
            setValue(formMeta.fields.country.name, countrySelectOptions[0]);
        }
    }, [countrySelectOptions, formMeta.fields.country.name, setValue]);

    const addressCityField = (
        <FormLine bottomGap>
            <Controller
                name={formMeta.fields.city.name}
                render={({ fieldState: { isTouched, invalid, error }, field }) => (
                    <>
                        <TextInput
                            required
                            fieldRef={field}
                            hasError={invalid}
                            id={formMeta.getFieldId(formMeta.fields.city.name)}
                            isTouched={isTouched}
                            label={formMeta.fields.city.label}
                            name={formMeta.fields.city.name}
                            type="text"
                        />
                        <FormLineError
                            data-testid={formMeta.formName + '-' + formMeta.fields.city.name + '-error'}
                            error={error}
                            inputType="text-input"
                        />
                    </>
                )}
            />
        </FormLine>
    );

    const addressZipField = (
        <FormLine bottomGap className="w-full flex-none lg:w-36">
            <Controller
                name={formMeta.fields.postcode.name}
                render={({ fieldState: { isTouched, invalid, error }, field }) => (
                    <>
                        <TextInput
                            required
                            fieldRef={field}
                            hasError={invalid}
                            id={formMeta.getFieldId(formMeta.fields.postcode.name)}
                            isTouched={isTouched}
                            label={formMeta.fields.postcode.label}
                            name={formMeta.fields.postcode.name}
                            type="text"
                        />
                        <FormLineError
                            data-testid={formMeta.formName + '-' + formMeta.fields.postcode.name + '-error'}
                            error={error}
                            inputType="text-input"
                        />
                    </>
                )}
            />
        </FormLine>
    );

    if (countrySelectOptions.length === 0) {
        return null;
    }

    return (
        <>
            <FormLine bottomGap>
                <Controller
                    name={formMeta.fields.firstName.name}
                    render={({ fieldState: { isTouched, invalid, error }, field }) => (
                        <>
                            <TextInput
                                required
                                fieldRef={field}
                                hasError={invalid}
                                id={formMeta.getFieldId(formMeta.fields.firstName.name)}
                                isTouched={isTouched}
                                label={formMeta.fields.firstName.label}
                                name={formMeta.fields.firstName.name}
                                type="text"
                            />
                            <FormLineError
                                data-testid={formMeta.formName + '-' + formMeta.fields.firstName.name + '-error'}
                                error={error}
                                inputType="text-input"
                            />
                        </>
                    )}
                />
            </FormLine>
            <FormLine bottomGap>
                <Controller
                    name={formMeta.fields.lastName.name}
                    render={({ fieldState: { isTouched, invalid, error }, field }) => (
                        <>
                            <TextInput
                                required
                                fieldRef={field}
                                hasError={invalid}
                                id={formMeta.getFieldId(formMeta.fields.lastName.name)}
                                isTouched={isTouched}
                                label={formMeta.fields.lastName.label}
                                name={formMeta.fields.lastName.name}
                                type="text"
                            />
                            <FormLineError
                                data-testid={formMeta.formName + '-' + formMeta.fields.lastName.name + '-error'}
                                error={error}
                                inputType="text-input"
                            />
                        </>
                    )}
                />
            </FormLine>
            <FormColumn>
                <FormLine bottomGap className={isItalianB2c ? 'w-full flex-none lg:w-3/5' : 'w-full'}>
                    <Controller
                        name={formMeta.fields.street.name}
                        render={({ fieldState: { isTouched, invalid, error }, field }) => (
                            <>
                                <TextInput
                                    required
                                    fieldRef={field}
                                    hasError={invalid}
                                    id={formMeta.getFieldId(formMeta.fields.street.name)}
                                    isTouched={isTouched}
                                    label={formMeta.fields.street.label}
                                    name={formMeta.fields.street.name}
                                    type="text"
                                />
                                <FormLineError
                                    data-testid={formMeta.formName + '-' + formMeta.fields.street.name + '-error'}
                                    error={error}
                                    inputType="text-input"
                                />
                            </>
                        )}
                    />
                </FormLine>
                {isItalianB2c && (
                    <FormLine bottomGap className="w-full flex-none lg:w-2/5">
                        <Controller
                            name={formMeta.fields.houseNumber.name}
                            render={({ fieldState: { isTouched, invalid, error }, field }) => (
                                <>
                                    <TextInput
                                        required
                                        fieldRef={field}
                                        hasError={invalid}
                                        id={formMeta.getFieldId(formMeta.fields.houseNumber.name)}
                                        isTouched={isTouched}
                                        label={formMeta.fields.houseNumber.label}
                                        name={formMeta.fields.houseNumber.name}
                                        type="text"
                                    />
                                    <FormLineError error={error} inputType="text-input" />
                                </>
                            )}
                        />
                    </FormLine>
                )}
            </FormColumn>
            {isItalianB2c && (
                <FormLine bottomGap>
                    <Controller
                        name={formMeta.fields.district.name}
                        render={({ fieldState: { invalid, error }, field }) => (
                            <>
                                <Select
                                    required
                                    fieldRef={field}
                                    hasError={invalid}
                                    id="districtSelector"
                                    label={formMeta.fields.district.label}
                                    options={districtSelectOptions}
                                    value={districtSelectOptions.find((option) => option.value === field.value.value)}
                                    onChange={field.onChange}
                                />
                                <FormLineError error={error} inputType="select" />
                            </>
                        )}
                    />
                </FormLine>
            )}
            <FormColumn>
                {cityFieldOrder === 1 ? (
                    <>
                        {addressCityField}
                        {addressZipField}
                    </>
                ) : (
                    <>
                        {addressZipField}
                        {addressCityField}
                    </>
                )}
            </FormColumn>
            <FormLine bottomGap>
                <Controller
                    name={formMeta.fields.country.name}
                    render={({ fieldState: { invalid, error }, field }) => (
                        <>
                            <Select
                                fieldRef={field}
                                hasError={invalid}
                                id="countrySelector"
                                label={formMeta.fields.country.label}
                                options={countrySelectOptions}
                                value={countrySelectOptions.find((option) => option.value === field.value.value)}
                                onChange={field.onChange}
                            />
                            <FormLineError
                                data-testid={formMeta.formName + '-' + formMeta.fields.country.name + '-error'}
                                error={error}
                                inputType="select"
                            />
                        </>
                    )}
                />
            </FormLine>
            <FormLine bottomGap>
                <Controller
                    name={formMeta.fields.telephone.name}
                    render={({ fieldState: { isTouched, invalid, error }, field }) => (
                        <>
                            <PhoneInput
                                required
                                fieldRef={field}
                                hasError={invalid}
                                id={formMeta.getFieldId(formMeta.fields.telephone.name)}
                                isTouched={isTouched}
                                label={formMeta.fields.telephone.label}
                                name={formMeta.fields.telephone.name}
                            />
                            <FormLineError
                                data-testid={formMeta.formName + '-' + formMeta.fields.telephone.name + '-error'}
                                error={error}
                                inputType="text-input"
                            />
                        </>
                    )}
                />
            </FormLine>
            <FormLine bottomGap>
                <Controller
                    name={formMeta.fields.birthDay.name}
                    render={({ fieldState: { isTouched, invalid, error }, field }) => (
                        <>
                            <DateSelector
                                dateFormat={['dd/MM/yyyy', 'dd/MM/yy', 'dd.MM.yy', 'dd.MM.yyyy']}
                                fieldRef={field}
                                hasError={invalid}
                                id={formMeta.formName + '-' + formMeta.fields.birthDay.name}
                                isTouched={isTouched}
                                label={formMeta.fields.birthDay.label}
                                maxDate={maximalDate}
                                minDate={minimalDate}
                                name={formMeta.formName + '-' + formMeta.fields.birthDay.name}
                                placeholderText={t('dd/mm/yyyy')}
                            />
                            <FormLineError
                                data-testid={formMeta.formName + '-' + formMeta.fields.birthDay.name + '-error'}
                                error={error}
                                inputType="text-input"
                            />
                        </>
                    )}
                />
            </FormLine>
            {isPortugalLocale && !formProviderMethods.getValues('companyCustomer') && (
                <FormLine bottomGap>
                    <Controller
                        name={formMeta.fields.companyNumber.name}
                        render={({ fieldState: { isTouched, invalid, error }, field }) => (
                            <>
                                <TextInput
                                    aria-label={createNumberAriaLabel(field.value)}
                                    fieldRef={field}
                                    hasError={invalid}
                                    id={formMeta.formName + '-' + formMeta.fields.companyNumber.name}
                                    isTouched={isTouched}
                                    label={formMeta.fields.companyNumber.label}
                                    name={formMeta.fields.companyNumber.name}
                                    type="text"
                                />
                                <FormLineError
                                    error={error}
                                    inputType="text-input"
                                    data-testid={
                                        formMeta.formName + '-' + formMeta.fields.companyNumber.name + '-error'
                                    }
                                />
                            </>
                        )}
                    />
                </FormLine>
            )}
            {!isAustriaLocale && (
                <FormLine>
                    <ChoiceFormLine isLastItem>
                        <Controller
                            name={formMeta.fields.companyCustomer.name}
                            render={({ field }) => (
                                <Checkbox
                                    fieldRef={field}
                                    id={formMeta.formName + '-' + formMeta.fields.companyCustomer.name}
                                    label={formMeta.fields.companyCustomer.label}
                                    name={formMeta.fields.companyCustomer.name}
                                />
                            )}
                        />
                    </ChoiceFormLine>
                </FormLine>
            )}
        </>
    );
};

/* @component */
export default User;
